import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ImagePlus, X, Loader2, Save, Trash2 } from "lucide-react";
import { getVehicle, adminCreateVehicle, adminUpdateVehicle, adminUploadImage, resolveImageUrl } from "../lib/api";
import { toast } from "sonner";

const BODY_TYPES = ["sedan", "suv", "coupe", "truck", "convertible", "hatchback", "wagon", "van"];
const FUEL_TYPES = ["gasoline", "diesel", "electric", "hybrid"];
const TRANSMISSIONS = ["automatic", "manual"];
const DRIVETRAINS = ["fwd", "rwd", "awd", "4wd"];

const empty = {
  make: "", model: "", year: new Date().getFullYear(), price: 0, mileage: 0,
  body_type: "sedan", transmission: "automatic", fuel_type: "gasoline",
  exterior_color: "", interior_color: "", engine: "", horsepower: 0,
  drivetrain: "fwd", vin: "", description: "", features: [], images: [],
  featured: false, sold: false,
};

export default function AdminVehicleForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const nav = useNavigate();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [featureInput, setFeatureInput] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    if (!isEdit) return;
    getVehicle(id).then((v) => {
      setForm({ ...empty, ...v });
    }).catch(() => toast.error("Failed to load vehicle"));
  }, [id, isEdit]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUploadImage(file);
      setForm((f) => ({ ...f, images: [...f.images, res.url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const addImageUrl = () => {
    const url = window.prompt("Image URL:");
    if (url) setForm((f) => ({ ...f, images: [...f.images, url] }));
  };

  const addFeature = () => {
    if (!featureInput.trim()) return;
    setForm((f) => ({ ...f, features: [...f.features, featureInput.trim()] }));
    setFeatureInput("");
  };

  const removeFeature = (idx) => {
    setForm((f) => ({ ...f, features: f.features.filter((_, i) => i !== idx) }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        year: Number(form.year),
        price: Number(form.price),
        mileage: Number(form.mileage),
        horsepower: Number(form.horsepower),
      };
      if (isEdit) {
        await adminUpdateVehicle(id, payload);
        toast.success("Vehicle updated");
      } else {
        await adminCreateVehicle(payload);
        toast.success("Vehicle created");
      }
      nav("/admin");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : (Array.isArray(detail) ? detail.map(d => d.msg).join(", ") : "Save failed");
      toast.error(msg);
    } finally { setSaving(false); }
  };

  return (
    <div data-testid="page-admin-vehicle-form" className="pt-32 pb-24 max-w-[1200px] mx-auto px-6 md:px-10">
      <Link to="/admin" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8">
        <ArrowLeft size={14} /> Back to inventory
      </Link>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="overline">{isEdit ? "Edit Vehicle" : "New Vehicle"}</div>
        <h1 className="font-display text-5xl md:text-7xl mt-3 tracking-tighter">
          {isEdit ? "Update the listing." : "Add to inventory."}
        </h1>
      </motion.div>

      <form onSubmit={onSubmit} className="mt-12 space-y-8" data-testid="vehicle-form">
        {/* Basics */}
        <Section title="Basics">
          <Grid>
            <Field label="Make"><input required data-testid="vf-make" value={form.make} onChange={(e) => setField("make", e.target.value)} /></Field>
            <Field label="Model"><input required data-testid="vf-model" value={form.model} onChange={(e) => setField("model", e.target.value)} /></Field>
            <Field label="Year"><input required type="number" data-testid="vf-year" value={form.year} onChange={(e) => setField("year", e.target.value)} /></Field>
            <Field label="VIN"><input required data-testid="vf-vin" value={form.vin} onChange={(e) => setField("vin", e.target.value.toUpperCase())} /></Field>
            <Field label="Price ($)"><input required type="number" data-testid="vf-price" value={form.price} onChange={(e) => setField("price", e.target.value)} /></Field>
            <Field label="Mileage"><input required type="number" data-testid="vf-mileage" value={form.mileage} onChange={(e) => setField("mileage", e.target.value)} /></Field>
          </Grid>
        </Section>

        {/* Specs */}
        <Section title="Specifications">
          <Grid>
            <Field label="Body type">
              <select data-testid="vf-body" value={form.body_type} onChange={(e) => setField("body_type", e.target.value)}>
                {BODY_TYPES.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Transmission">
              <select data-testid="vf-transmission" value={form.transmission} onChange={(e) => setField("transmission", e.target.value)}>
                {TRANSMISSIONS.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Fuel type">
              <select data-testid="vf-fuel" value={form.fuel_type} onChange={(e) => setField("fuel_type", e.target.value)}>
                {FUEL_TYPES.map((b) => <option key={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="Drivetrain">
              <select data-testid="vf-drivetrain" value={form.drivetrain} onChange={(e) => setField("drivetrain", e.target.value)}>
                {DRIVETRAINS.map((b) => <option key={b}>{b.toUpperCase()}</option>)}
              </select>
            </Field>
            <Field label="Engine"><input data-testid="vf-engine" value={form.engine} onChange={(e) => setField("engine", e.target.value)} placeholder="e.g. 2.5L I4" /></Field>
            <Field label="Horsepower"><input type="number" data-testid="vf-hp" value={form.horsepower} onChange={(e) => setField("horsepower", e.target.value)} /></Field>
            <Field label="Exterior color"><input data-testid="vf-ext" value={form.exterior_color} onChange={(e) => setField("exterior_color", e.target.value)} /></Field>
            <Field label="Interior color"><input data-testid="vf-int" value={form.interior_color} onChange={(e) => setField("interior_color", e.target.value)} /></Field>
          </Grid>
        </Section>

        {/* Description */}
        <Section title="Description">
          <textarea rows={5} data-testid="vf-desc" value={form.description} onChange={(e) => setField("description", e.target.value)} placeholder="Notable options, condition, history..." />
        </Section>

        {/* Features */}
        <Section title="Features">
          <div className="flex gap-2">
            <input value={featureInput} onChange={(e) => setFeatureInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addFeature())} placeholder="e.g. Heated Seats" data-testid="vf-feature-input" />
            <button type="button" onClick={addFeature} className="btn-outline" data-testid="vf-add-feature">Add</button>
          </div>
          {form.features.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {form.features.map((f, i) => (
                <span key={i} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-sm">
                  {f}
                  <button type="button" onClick={() => removeFeature(i)} className="text-white/40 hover:text-white"><X size={12} /></button>
                </span>
              ))}
            </div>
          )}
        </Section>

        {/* Images */}
        <Section title="Images">
          <div className="flex flex-wrap gap-3">
            {form.images.map((img, i) => (
              <div key={i} className="relative w-40 h-28 rounded-lg overflow-hidden border border-white/10 group">
                <img src={resolveImageUrl(img)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <X size={12} />
                </button>
              </div>
            ))}
            <label className="w-40 h-28 rounded-lg border border-dashed border-white/20 hover:border-[var(--primary)] flex flex-col items-center justify-center gap-2 cursor-pointer text-white/50 hover:text-white transition-colors">
              {uploading ? <Loader2 size={20} className="animate-spin" /> : <ImagePlus size={20} />}
              <span className="text-xs">{uploading ? "Uploading..." : "Upload photo"}</span>
              <input ref={fileRef} data-testid="vf-upload" type="file" accept="image/*" onChange={onUpload} className="hidden" />
            </label>
          </div>
          <button type="button" onClick={addImageUrl} className="mt-4 text-xs text-white/50 hover:text-white underline">Or add image by URL</button>
        </Section>

        {/* Status */}
        <Section title="Status">
          <div className="flex gap-4 flex-wrap">
            <Toggle label="Featured on homepage" checked={form.featured} onChange={(v) => setField("featured", v)} testid="vf-featured" />
            <Toggle label="Sold" checked={form.sold} onChange={(v) => setField("sold", v)} testid="vf-sold" />
          </div>
        </Section>

        <div className="flex gap-3 pt-6">
          <button type="submit" disabled={saving} className="btn-primary" data-testid="vf-save">
            {saving ? "Saving..." : (<><Save size={16} /> {isEdit ? "Save Changes" : "Create Vehicle"}</>)}
          </button>
          <button type="button" onClick={() => nav("/admin")} className="btn-outline">Cancel</button>
        </div>
      </form>
    </div>
  );
}

const Section = ({ title, children }) => (
  <div>
    <div className="overline mb-4">{title}</div>
    {children}
  </div>
);

const Grid = ({ children }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
);

const Field = ({ label, children }) => (
  <div>
    <label>{label}</label>
    {children}
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center gap-3 cursor-pointer">
    <button type="button" data-testid={testid} onClick={() => onChange(!checked)}
      className={`w-11 h-6 rounded-full border transition-colors ${checked ? "bg-[var(--primary)] border-[var(--primary)]" : "bg-white/5 border-white/10"}`}>
      <span className={`block w-4 h-4 bg-white rounded-full transition-transform mt-[3px] ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
    <span className="text-sm text-white/70">{label}</span>
  </label>
);
